import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormHelperText,
  Tooltip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { GET, PUT } from "APIclient";
import properties from "constants/ApplicationConstants";

const RATING_LEGEND = [
  { points: 1, rating: "Unsatisfactory", comment: "Performance is consistently non acceptable" },
  { points: 2, rating: "Below Expectations", comment: "Performance fails to meet job requirement on frequent basis" },
  { points: 3, rating: "Meets Expectations", comment: "Performance is regularly competent & dependable" },
  { points: 4, rating: "Exceeds Expectations", comment: "Performance is routinely above expectation" },
  { points: 5, rating: "Outstanding", comment: "Performance is consistently superior" },
];

// Shared row height for score + comment cells
const ROW_HEIGHT = 36;

const colStyle = {
  display: "flex",
  alignItems: "center",
  minHeight: ROW_HEIGHT,
};

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box p={0.5}>{children}</Box>}
    </div>
  );
}

// ─── FIX: Parse date string as local date to avoid UTC offset issues ──────────
// new Date("2026-04-24") is parsed as UTC midnight, which in IST (UTC+5:30)
// becomes 05:30 AM — making "today < startDate" true even when dates match.
// This helper constructs the date in local time so comparisons are correct.
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day); // local midnight, no timezone shift
};
// ─────────────────────────────────────────────────────────────────────────────

function EmployeePerformanceForm() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [notification, setNotification] = useState({ open: false, message: "", type: "" });

  const [employeeData, setEmployeeData] = useState(null);
  const [appraisalData, setAppraisalData] = useState(null);
  const [empId, setEmpId] = useState(null);
  const [appraisalId, setAppraisalId] = useState(null);
  const [empPermission, setEmpPermission] = useState(false);
  const [managerPermission, setManagerPermission] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [noAppraisalMessage, setNoAppraisalMessage] = useState(null);

  // KRA date-window state
  const [kraDateStatus, setKraDateStatus] = useState(null); // "before" | "after" | "open" | null
  const [kraStartDate, setKraStartDate] = useState(null);
  const [kraEndDate, setKraEndDate] = useState(null);

  const [goalsData, setGoalsData] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const [employeeRatings, setEmployeeRatings] = useState({
    technicalPrimaryScores: [],
    technicalPrimaryComments: [],
    technicalSecondaryScores: [],
    technicalSecondaryComments: [],
    competencyPrimaryScores: [],
    competencyPrimaryComments: [],
    competencySecondaryScores: [],
    competencySecondaryComments: [],
    questionnaireAnswers: [],
  });

  const [commentErrors, setCommentErrors] = useState({
    technicalPrimaryComments: [],
    technicalSecondaryComments: [],
    competencyPrimaryComments: [],
    competencySecondaryComments: [],
  });

  const [questionnaireErrors, setQuestionnaireErrors] = useState([]);

  const canEdit =
    empPermission &&
    !isViewMode &&
    (kraDateStatus === "open" || kraDateStatus === null);

  useEffect(() => {
    const storedEmpId = localStorage.getItem("empId") || sessionStorage.getItem("empId");
    if (location.state?.appraisalId && location.state?.empId) {
      const passedAppraisalId = location.state.appraisalId;
      const passedEmpId = location.state.empId;
      const viewMode = location.state.viewMode || false;
      setEmpId(passedEmpId);
      setAppraisalId(passedAppraisalId);
      setIsViewMode(viewMode);
      Promise.all([
        fetchEmployeeDetails(passedAppraisalId, passedEmpId),
        fetchGoals(passedAppraisalId, passedEmpId),
      ]).finally(() => setLoading(false));
    } else if (storedEmpId) {
      const parsedEmpId = parseInt(storedEmpId);
      setEmpId(parsedEmpId);
      fetchCurrentAppraisal(parsedEmpId);
    } else {
      showNotification("Employee ID not found", "error");
      setLoading(false);
    }
  }, [location.state]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    // Use parseLocalDate so the displayed date also matches the intended local date
    const d = parseLocalDate(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const fetchCurrentAppraisal = async (employeeId) => {
    try {
      setLoading(true);
      const response = await GET(`manager/appraisal/current`);
      if (response.data?.data) {
        const permissionData = response.data.data;
        setAppraisalId(permissionData.appraisalId);

        const today = new Date();
        today.setHours(0, 0, 0, 0); // local midnight

        // ─── FIX: Use parseLocalDate instead of new Date() ───────────────────
        // Previously: new Date("2026-04-24") → UTC midnight → IST 05:30 AM
        // causing today (local midnight IST) < startDate (UTC midnight) = true
        // even when both represent the same calendar date.
        const startDate = parseLocalDate(permissionData.employeeKraStartDate);
        const endDate = parseLocalDate(permissionData.employeeKraEndDate);
        // ─────────────────────────────────────────────────────────────────────

        let status = "open";
        if (startDate && today < startDate) {
          status = "before";
        } else if (endDate && today > endDate) {
          status = "after";
        }

        setKraDateStatus(status);
        setKraStartDate(permissionData.employeeKraStartDate);
        setKraEndDate(permissionData.employeeKraEndDate);

        await Promise.all([
          fetchEmployeeDetails(permissionData.appraisalId, employeeId),
          fetchGoals(permissionData.appraisalId, employeeId),
        ]);
      } else if (response.data?.statusCode === 400) {
        setNoAppraisalMessage(response.data.message || "No active appraisal found.");
        setLoading(false);
      }
    } catch (error) {
      setNoAppraisalMessage(
        error.response?.data?.message || "Failed to load current appraisal. Please try again later."
      );
      setLoading(false);
    }
  };

  const fetchEmployeeDetails = async (appraisalId, employeeId) => {
    try {
      const response = await GET(`manager/${appraisalId}/employee/${employeeId}/view`);
      if (response.data?.data) {
        const data = response.data.data;
        setEmpPermission(isViewMode ? false : (data?.empPermission || false));
        setManagerPermission(data.managerPermission || false);
        const nameParts = (data.employeeName || "").split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
        const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : "";
        setEmployeeData({
          empId: data?.empId || "",
          firstName,
          middleName,
          lastName,
          officialEmailAddress: data.email || "",
          gender: data.gender || "",
          maritalStatus: data.maritalStatus === "unmarried" ? "Unmarried" : "Married" || "",
          birthDate: data.dob || "",
          mobileNo: data.mobileNo || "",
          alterMobileNo: data.alternativeMobileNumber || "",
        });
        setViewData(data);
        setAppraisalData((prev) => ({ ...prev, questionnaire: data.questionnaire || [] }));
        setEmployeeRatings((prev) => ({
          ...prev,
          questionnaireAnswers: (data.questionnaire || []).map((q) => q.description || ""),
        }));
      }
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to load employee details", "error");
    }
  };

  const fetchGoals = async (appraisalId, employeeId) => {
    try {
      const response = await GET(`manager/${appraisalId}/employee/${employeeId}/goals`);
      if (response.data?.data) setGoalsData(response.data.data);
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to load goals", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (goalsData && viewData) mergeSkillsData();
  }, [goalsData, viewData]);

  const mergeSkillsData = () => {
    const mergeSkills = (goalsSkills, viewSkills) =>
      (goalsSkills || []).map((goalSkill, index) => {
        const viewSkill = (viewSkills || [])[index] || {};
        return {
          skillName: goalSkill.skillSet,
          paFormId: goalSkill.paFormId,
          empScore: viewSkill.empScore || "",
          empComment: viewSkill.empComment || "",
          appraiserScore: viewSkill.appraiserScore || "",
          appraiserComment: viewSkill.appraiserComment || "",
        };
      });

    const transformedQuestionnaire = (goalsData?.questionnaire || []).map((q, index) => {
      const viewQuestion = (viewData?.questionnaire || [])[index] || {};
      return { paFormId: q.paFormId, question: q.skillSet, answer: viewQuestion.description || "" };
    });

    const transformedSkills = {
      technicalPrimarySkills: mergeSkills(goalsData?.technicalPrimary, viewData?.technicalSkills?.primary),
      technicalSecondarySkills: mergeSkills(goalsData?.technicalSecondary, viewData?.technicalSkills?.secondary),
      competencyPrimarySkills: mergeSkills(goalsData?.competencyPrimary, viewData?.competencySkills?.primary),
      competencySecondarySkills: mergeSkills(goalsData?.competencySecondary, viewData?.competencySkills?.secondary),
      questionnaire: transformedQuestionnaire,
    };

    setAppraisalData((prev) => ({ ...prev, ...transformedSkills }));
    initializeEmployeeRatings(transformedSkills);
  };

  const initializeEmployeeRatings = (data) => {
    setEmployeeRatings({
      technicalPrimaryScores: data?.technicalPrimarySkills.map((s) => s.empScore || ""),
      technicalPrimaryComments: data?.technicalPrimarySkills.map((s) => s.empComment || ""),
      technicalSecondaryScores: data?.technicalSecondarySkills.map((s) => s.empScore || ""),
      technicalSecondaryComments: data?.technicalSecondarySkills.map((s) => s.empComment || ""),
      competencyPrimaryScores: data?.competencyPrimarySkills.map((s) => s.empScore || ""),
      competencyPrimaryComments: data?.competencyPrimarySkills.map((s) => s.empComment || ""),
      competencySecondaryScores: data?.competencySecondarySkills.map((s) => s.empScore || ""),
      competencySecondaryComments: data?.competencySecondarySkills.map((s) => s.empComment || ""),
      questionnaireAnswers: (data?.questionnaire || []).map((q) => q.answer || ""),
    });
    setCommentErrors({
      technicalPrimaryComments: data?.technicalPrimarySkills.map(() => ""),
      technicalSecondaryComments: data?.technicalSecondarySkills.map(() => ""),
      competencyPrimaryComments: data?.competencyPrimarySkills.map(() => ""),
      competencySecondaryComments: data?.competencySecondarySkills.map(() => ""),
    });
    setQuestionnaireErrors((data?.questionnaire || []).map(() => ""));
  };

  const showNotification = (message, type = "info") => {
    setNotification({ open: true, message, type });
    setTimeout(() => setNotification({ open: false, message: "", type: "" }), 4000);
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);
  const isCommentOptionalForScore = (score) => {
    const p = parseInt(score);
    return p === 3 || p === 4;
  };

  const validateText = (value) => {
    if (!value || value === "") return { valid: true, error: "" };
    if (value.trim() === "") return { valid: false, error: "Cannot enter only spaces" };
    if (!/[a-zA-Z0-9]/.test(value)) return { valid: false, error: "Cannot enter only special characters" };
    if (/^[0-9\s\W]*$/.test(value) && /[0-9]/.test(value) && !/[a-zA-Z]/.test(value))
      return { valid: false, error: "Cannot enter only numbers" };
    return { valid: true, error: "" };
  };

  const handleScoreChange = (category, index, value) => {
    if (!canEdit) return;
    setEmployeeRatings((prev) => {
      const s = [...prev[`${category}Scores`]];
      s[index] = value;
      return { ...prev, [`${category}Scores`]: s };
    });
  };

  const handleCommentChange = (category, index, value) => {
    if (!canEdit) return;
    const validation = validateText(value);
    setCommentErrors((prev) => {
      const e = [...prev[`${category}Comments`]];
      e[index] = validation.error;
      return { ...prev, [`${category}Comments`]: e };
    });
    if (validation.valid || value === "") {
      setEmployeeRatings((prev) => {
        const c = [...prev[`${category}Comments`]];
        c[index] = value;
        return { ...prev, [`${category}Comments`]: c };
      });
    }
  };

  const handleQuestionnaireChange = (index, value) => {
    if (!canEdit) return;
    const validation = validateText(value);
    setQuestionnaireErrors((prev) => {
      const e = [...prev];
      e[index] = validation.error;
      return e;
    });
    if (validation.valid || value === "") {
      setEmployeeRatings((prev) => {
        const a = [...prev.questionnaireAnswers];
        a[index] = value;
        return { ...prev, questionnaireAnswers: a };
      });
    }
  };

  const validateRatings = () => {
    const scoreKeys = [
      "technicalPrimaryScores",
      "technicalSecondaryScores",
      "competencyPrimaryScores",
      "competencySecondaryScores",
    ];
    for (const key of scoreKeys) {
      if (employeeRatings[key].some((s) => !s || s === "")) {
        showNotification("Please provide scores for all skills", "error");
        return false;
      }
    }
    const commentCategories = [
      { key: "technicalPrimaryComments", scoreKey: "technicalPrimaryScores", name: "Technical Primary Skills" },
      { key: "technicalSecondaryComments", scoreKey: "technicalSecondaryScores", name: "Technical Secondary Skills" },
      { key: "competencyPrimaryComments", scoreKey: "competencyPrimaryScores", name: "Competency Primary Skills" },
      { key: "competencySecondaryComments", scoreKey: "competencySecondaryScores", name: "Competency Secondary Skills" },
    ];
    for (const { key, scoreKey, name } of commentCategories) {
      const missing = employeeRatings[key]
        .map((c, i) =>
          (!c || !c.trim()) && !isCommentOptionalForScore(parseInt(employeeRatings[scoreKey][i])) ? i + 1 : null
        )
        .filter(Boolean);
      if (missing.length > 0) {
        showNotification(`Please provide comments for all ${name}. Missing: ${missing.join(", ")}`, "error");
        if (key.includes("technical")) setTabValue(0);
        else setTabValue(1);
        return false;
      }
    }
    if (Object.values(commentErrors).some((e) => e.some((err) => err !== ""))) {
      showNotification("Please fix all comment errors before submitting", "error");
      return false;
    }
    if (questionnaireErrors.some((e) => e !== "")) {
      showNotification("Please fix all questionnaire errors before submitting", "error");
      setTabValue(2);
      return false;
    }
    const unanswered = employeeRatings.questionnaireAnswers
      .map((a, i) => (!a?.trim() ? i + 1 : null))
      .filter(Boolean);
    if (unanswered.length > 0) {
      showNotification(`Please answer all questions. Missing: Question ${unanswered.join(", ")}`, "error");
      setTabValue(2);
      return false;
    }
    return true;
  };

  const buildPayload = (status) => ({
    appraisalId: parseInt(appraisalId),
    empId: parseInt(empId),
    ratingType: "EMPLOYEE",
    technicalRatings: {
      primaryScores: employeeRatings.technicalPrimaryScores.map((s) => (s ? parseInt(s) : null)),
      primaryComments: employeeRatings.technicalPrimaryComments,
      primarySkillSetIds: appraisalData?.technicalPrimarySkills.map((s) => s.paFormId),
      secondaryScores: employeeRatings.technicalSecondaryScores.map((s) => (s ? parseInt(s) : null)),
      secondaryComments: employeeRatings.technicalSecondaryComments,
      secondarySkillSetIds: appraisalData?.technicalSecondarySkills.map((s) => s.paFormId),
    },
    competencyRatings: {
      primaryScores: employeeRatings.competencyPrimaryScores.map((s) => (s ? parseInt(s) : null)),
      primaryComments: employeeRatings.competencyPrimaryComments,
      primarySkillSetIds: appraisalData?.competencyPrimarySkills.map((s) => s.paFormId),
      secondaryScores: employeeRatings.competencySecondaryScores.map((s) => (s ? parseInt(s) : null)),
      secondaryComments: employeeRatings.competencySecondaryComments,
      secondarySkillSetIds: appraisalData?.competencySecondarySkills.map((s) => s.paFormId),
    },
    questionnaireAnswers: employeeRatings.questionnaireAnswers,
    questionnaireSkillSetIds: appraisalData?.questionnaire.map((q) => q.paFormId),
    submitStatus: status,
  });

  const handleSaveRatings = () => {
    if (!canEdit) {
      showNotification("You don't have permission to edit this form", "error");
      return;
    }
    setShowValidationErrors(true);
    if (!validateRatings()) return;
    setOpenConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setOpenConfirmDialog(false);
    try {
      setSaving(true);
      const payload = buildPayload("save");
      const response = await PUT(`manager/save-ratings`, payload);
      if (response.data?.statusCode === 200) {
        showNotification("Ratings saved successfully", "success");
        setTimeout(() => navigate(-1), 2000);
      } else if (response.data?.statusCode === 400) {
        showNotification(response.data.message, "error");
      }
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to save ratings", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsDraft = async () => {
    if (!canEdit) {
      showNotification("You don't have permission to edit this form", "error");
      return;
    }
    try {
      setSavingDraft(true);
      const payload = buildPayload("draft");
      const response = await PUT(`manager/save-ratings`, payload);
      if (response.data?.statusCode === 200) {
        showNotification("Draft saved successfully", "success");
      } else if (response.data?.statusCode === 400) {
        showNotification(response.data.message, "error");
      }
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to save draft", "error");
    } finally {
      setSavingDraft(false);
    }
  };

  const scoreSx = {
    height: ROW_HEIGHT,
    fontSize: "0.8rem",
    "& .MuiSelect-select": { py: "6px", color: "#000 !important" },
    "&.Mui-disabled .MuiSelect-select": { color: "#000 !important", WebkitTextFillColor: "#000 !important" },
  };

  const commentSx = {
    "& .MuiOutlinedInput-root": { height: ROW_HEIGHT, alignItems: "center" },
    "& .MuiInputBase-input": {
      py: "6px",
      fontSize: "0.8rem",
      color: "#000 !important",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000 !important", color: "#000 !important" },
  };

  const renderSkillsTable = (skills, title, category) => {
    if (!skills?.length) {
      return (
        <Alert severity="warning" sx={{ my: 0.5 }}>
          <MDTypography variant="body2">No {title} Added.</MDTypography>
        </Alert>
      );
    }

    const scoresKey = `${category}Scores`;
    const commentsKey = `${category}Comments`;

    const COL = { num: 0.4, skill: 2.2, empScore: 1.2, empComment: 3.4, mgrScore: 1.2, mgrComment: 3.6 };
    const headerCellSx = { fontSize: "0.72rem", fontWeight: 700, color: "#444", lineHeight: 1.2 };

    return (
      <Box sx={{ border: "1px solid #ddd", borderRadius: 1, overflow: "hidden", mb: 1 }}>
        <Grid container sx={{ backgroundColor: "#f0f4f8", px: 0.75, py: 0.5 }} alignItems="center">
          <Grid item xs={COL.num} sx={colStyle}>
            <Typography sx={headerCellSx}>#</Typography>
          </Grid>
          <Grid item xs={COL.skill} sx={colStyle}>
            <Typography sx={headerCellSx}>Skill Set</Typography>
          </Grid>
          <Grid item xs={COL.empScore} sx={{ ...colStyle, justifyContent: "center" }}>
            <Typography sx={{ ...headerCellSx, textAlign: "center" }}>
              My Score{!isViewMode && <span style={{ color: "red" }}> *</span>}
            </Typography>
          </Grid>
          <Grid item xs={COL.empComment} sx={colStyle}>
            <Typography sx={headerCellSx}>
              My Comments
              {!isViewMode && (
                <span style={{ color: "red", fontSize: "0.65rem" }}> * (required for score 1,2,5)</span>
              )}
            </Typography>
          </Grid>
          <Grid item xs={COL.mgrScore} sx={{ ...colStyle, justifyContent: "center" }}>
            <Typography sx={{ ...headerCellSx, textAlign: "center" }}>Mgr Score</Typography>
          </Grid>
          <Grid item xs={COL.mgrComment} sx={colStyle}>
            <Typography sx={headerCellSx}>Manager Comment</Typography>
          </Grid>
        </Grid>

        {skills.map((skill, index) => {
          const currentScore = employeeRatings[scoresKey][index];
          const isOptional = isCommentOptionalForScore(currentScore);
          const currentComment = employeeRatings[commentsKey][index] || "";
          const commentError = commentErrors[commentsKey]?.[index] || "";
          const showCommentError =
            !!commentError ||
            (showValidationErrors && currentComment === "" && canEdit && !isOptional);
          const commentHelperText = commentError
            ? commentError
            : showValidationErrors && currentComment === "" && canEdit && !isOptional
              ? "Required"
              : "";

          return (
            <Grid
              container
              key={index}
              alignItems="center"
              sx={{
                px: 0.75,
                py: 0.25,
                backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                borderTop: "1px solid #eee",
                "&:hover": { backgroundColor: "#f5f9ff" },
                minHeight: ROW_HEIGHT + (commentHelperText ? 18 : 0),
              }}
            >
              <Grid item xs={COL.num} sx={colStyle}>
                <Typography variant="body2" sx={{ fontSize: "0.78rem", color: "#666" }}>
                  {index + 1}
                </Typography>
              </Grid>

              <Grid item xs={COL.skill} sx={colStyle}>
                <Typography variant="body2" sx={{ fontSize: "0.8rem", wordBreak: "break-word", lineHeight: 1.3 }}>
                  {skill.skillName}
                </Typography>
              </Grid>

              <Grid item xs={COL.empScore} sx={{ ...colStyle, justifyContent: "center" }}>
                <FormControl size="small" disabled={!canEdit} sx={{ width: 68 }}>
                  <Select
                    value={currentScore || ""}
                    onChange={(e) => handleScoreChange(category, index, e.target.value)}
                    displayEmpty
                    MenuProps={{ disableScrollLock: true }}
                    sx={scoreSx}
                  >
                    <MenuItem value="" sx={{ fontSize: "0.8rem" }}>-</MenuItem>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <MenuItem key={s} value={s} sx={{ fontSize: "0.8rem" }}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={COL.empComment} sx={{ ...colStyle, flexDirection: "column", alignItems: "stretch" }}>
                <Tooltip
                  title={currentComment || ""}
                  placement="top"
                  arrow
                  disableHoverListener={!currentComment}
                  PopperProps={{ modifiers: [{ name: "offset", options: { offset: [0, -4] } }] }}
                >
                  <span style={{ width: "100%" }}>
                    <TextField
                      fullWidth
                      value={currentComment}
                      onChange={(e) => handleCommentChange(category, index, e.target.value)}
                      size="small"
                      variant="outlined"
                      placeholder={canEdit ? (isOptional ? "Optional" : "Required") : "-"}
                      disabled={!canEdit}
                      error={showCommentError}
                      sx={commentSx}
                      inputProps={{ style: { fontSize: "0.8rem" } }}
                    />
                  </span>
                </Tooltip>
                {commentHelperText && (
                  <FormHelperText error sx={{ mx: 0, mt: 0, fontSize: "0.65rem", lineHeight: 1.2 }}>
                    {commentHelperText}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={COL.mgrScore} sx={{ ...colStyle, justifyContent: "center" }}>
                <FormControl size="small" disabled sx={{ width: 68 }}>
                  <Select
                    value={!managerPermission ? skill.appraiserScore : ""}
                    displayEmpty
                    MenuProps={{ disableScrollLock: true }}
                    sx={scoreSx}
                  >
                    <MenuItem value="" sx={{ fontSize: "0.8rem" }}>-</MenuItem>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <MenuItem key={s} value={s} sx={{ fontSize: "0.8rem" }}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={COL.mgrComment} sx={colStyle}>
                <Tooltip
                  title={!managerPermission ? (skill.appraiserComment || "") : ""}
                  placement="top"
                  arrow
                  disableHoverListener={managerPermission || !skill.appraiserComment}
                >
                  <span style={{ width: "100%" }}>
                    <TextField
                      fullWidth
                      value={!managerPermission ? skill.appraiserComment : ""}
                      disabled
                      size="small"
                      variant="outlined"
                      placeholder="-"
                      sx={commentSx}
                      inputProps={{ style: { fontSize: "0.8rem" } }}
                    />
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          );
        })}
      </Box>
    );
  };

  const renderQuestionnaire = (questionnaire) => {
    if (!questionnaire?.length) {
      return (
        <Alert severity="warning">
          <MDTypography variant="body2">No Questionnaire Added.</MDTypography>
        </Alert>
      );
    }
    return (
      <MDBox>
        {questionnaire.map((item, index) => {
          const answer = employeeRatings.questionnaireAnswers[index] || "";
          const validationError = questionnaireErrors[index] || "";
          const isEmpty = !answer.trim();
          const showError = validationError || (showValidationErrors && isEmpty && canEdit);
          const errorMessage =
            validationError || (showValidationErrors && isEmpty ? "Please answer this question." : "");
          return (
            <MDBox key={index} mb={0.75}>
              <MDTypography
                variant="body2"
                mb={0.25}
                color="dark"
                fontWeight="medium"
                sx={{ fontSize: "0.82rem" }}
              >
                {item.question}
                {!isViewMode && <span style={{ color: "red" }}> *</span>}
              </MDTypography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={answer}
                onChange={(e) => handleQuestionnaireChange(index, e.target.value)}
                variant="outlined"
                size="small"
                placeholder={canEdit ? "Type your answer here... (required)" : "-"}
                disabled={!canEdit}
                error={!!showError}
                helperText={showError ? errorMessage : ""}
                required={!isViewMode}
                sx={{
                  "& .MuiInputBase-input": { color: "#000 !important", fontSize: "0.82rem" },
                  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000 !important" },
                }}
              />
            </MDBox>
          );
        })}
      </MDBox>
    );
  };

  // ── Loading state ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  // ── No active appraisal state ────────────────────────────────────────────────
  if (noAppraisalMessage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
          gap={2}
          px={2}
        >
          <Box
            sx={{
              textAlign: "center",
              p: 4,
              borderRadius: 3,
              border: "1px solid #e0e0e0",
              backgroundColor: "#fafafa",
              maxWidth: 480,
              width: "100%",
            }}
          >
            <Typography sx={{ fontSize: "3rem", mb: 1 }}>📋</Typography>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: "#333" }}>
              No Active Appraisal Cycle
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mb: 2.5, fontSize: "0.88rem" }}>
              {noAppraisalMessage}
              <br />
              Please check back later or contact your HR team for more information.
            </Typography>
            <MDButton variant="outlined" color="info" size="small" onClick={() => navigate(-1)}>
              ← Go Back
            </MDButton>
          </Box>
        </MDBox>
      </DashboardLayout>
    );
  }
  // ────────────────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={0.5} pb={1.5}>
        <Card>
          {/* Header */}
          <MDBox
            variant="gradient"
            sx={{ background: properties.colors.bgcolorbluethem }}
            borderRadius="lg"
            coloredShadow="success"
            mx={1.5}
            mt={0.75}
            p={0.75}
            textAlign="center"
          >
            <MDTypography variant="h5" fontWeight="medium" color="white" sx={{ fontSize: "1rem" }}>
              {isViewMode ? "Previous Appraisal Form - View Only" : "Employee Performance Evaluation Form"}
            </MDTypography>
            <MDTypography variant="body2" color="white" opacity={0.85} sx={{ fontSize: "0.75rem" }}>
              {isViewMode
                ? "Viewing previous appraisal - No edits allowed"
                : canEdit
                  ? "Complete your self-assessment"
                  : "View Only - No edit permission"}
            </MDTypography>
          </MDBox>

          <MDBox p={0.75}>
            {/* KRA Date-Window Banners */}
            {kraDateStatus === "before" && (
              <Alert severity="warning" sx={{ mb: 0.75, fontSize: "0.82rem" }}>
                <strong>Form not yet available.</strong> The self-assessment window opens on{" "}
                <strong>{formatDate(kraStartDate)}</strong>. You can view the form but cannot make
                any edits until then.
              </Alert>
            )}
            {kraDateStatus === "after" && (
              <Alert severity="error" sx={{ mb: 0.75, fontSize: "0.82rem" }}>
                <strong>Submission window has closed.</strong> The self-assessment deadline was{" "}
                <strong>{formatDate(kraEndDate)}</strong>. You can view the form but edits are no
                longer allowed.
              </Alert>
            )}

            {/* Rating Legend */}
            <Card variant="outlined" sx={{ mb: 0.75 }}>
              <MDBox
                px={0.75}
                pt={0.5}
                pb={0.25}
                sx={{ backgroundColor: "#f7f7f7", borderBottom: "1px solid #e0e0e0" }}
              >
                <MDTypography variant="subtitle1" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                  Performance Evaluation Form
                </MDTypography>
              </MDBox>
              <MDBox px={0.75} py={0.5}>
                <MDTypography variant="body2" mb={0.5} color="black" sx={{ fontSize: "0.78rem" }}>
                  Use the following ratings for consistency.
                </MDTypography>
                <Grid
                  container
                  sx={{
                    backgroundColor: "#f0f4f8",
                    px: 0.5,
                    py: 0.3,
                    borderRadius: "4px 4px 0 0",
                    border: "1px solid #ddd",
                  }}
                >
                  <Grid item xs={1}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Points</Typography>
                  </Grid>
                  <Grid item xs={2.5}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Ratings</Typography>
                  </Grid>
                  <Grid item xs={8.5}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Comments</Typography>
                  </Grid>
                </Grid>
                {RATING_LEGEND.map((row, index) => (
                  <Grid
                    container
                    key={row.points}
                    sx={{
                      px: 0.5,
                      py: 0.2,
                      backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                      border: "1px solid #ddd",
                      borderTop: "none",
                      borderRadius: index === RATING_LEGEND.length - 1 ? "0 0 4px 4px" : 0,
                    }}
                    alignItems="center"
                  >
                    <Grid item xs={1}>
                      <Typography sx={{ fontSize: "0.78rem" }}>{row.points}</Typography>
                    </Grid>
                    <Grid item xs={2.5}>
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#222" }}>
                        {row.rating}
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <Typography sx={{ fontSize: "0.78rem", color: "#333" }}>{row.comment}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </MDBox>
            </Card>

            {/* Tabs */}
            <Card variant="outlined" sx={{ mb: 0.75 }}>
              <Box sx={{ borderBottom: "1px solid #ddd" }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  textColor="info"
                  indicatorColor="info"
                  variant="fullWidth"
                  sx={{
                    minHeight: 38,
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "0.82rem",
                      minHeight: 38,
                      py: 0,
                    },
                    "& .Mui-selected": { color: "#2196f3 !important" },
                  }}
                >
                  <Tab label="Technical Skills" />
                  <Tab label="Performance Competency" />
                  <Tab label="Questionnaire" />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.25} sx={{ fontSize: "0.82rem" }}>
                  Technical Primary Skills
                </MDTypography>
                {renderSkillsTable(
                  appraisalData?.technicalPrimarySkills,
                  "Technical Primary Skills",
                  "technicalPrimary"
                )}
                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.25} sx={{ fontSize: "0.82rem" }}>
                  Technical Secondary Skills
                </MDTypography>
                {renderSkillsTable(
                  appraisalData?.technicalSecondarySkills,
                  "Technical Secondary Skills",
                  "technicalSecondary"
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.25} sx={{ fontSize: "0.82rem" }}>
                  Competency Primary Skills
                </MDTypography>
                {renderSkillsTable(
                  appraisalData?.competencyPrimarySkills,
                  "Competency Primary Skills",
                  "competencyPrimary"
                )}
                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.25} sx={{ fontSize: "0.82rem" }}>
                  Competency Secondary Skills
                </MDTypography>
                {renderSkillsTable(
                  appraisalData?.competencySecondarySkills,
                  "Competency Secondary Skills",
                  "competencySecondary"
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.5} sx={{ fontSize: "0.82rem" }}>
                  Questionnaire
                </MDTypography>
                {renderQuestionnaire(appraisalData?.questionnaire)}
              </TabPanel>
            </Card>

            {/* Action Buttons */}
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
              <MDButton variant="outlined" color="secondary" size="small" onClick={() => navigate(-1)}>
                {isViewMode ? "Back" : "Cancel"}
              </MDButton>
              {!isViewMode && (
                <MDButton
                  variant="gradient"
                  color="success"
                  size="small"
                  onClick={handleSaveRatings}
                  disabled={savingDraft || saving || !canEdit}
                  sx={{ background: properties.colors.bgcolorbluethem, mr: 20 }}
                >
                  {saving ? <CircularProgress size={16} color="inherit" /> : "Submit Self-Assessment"}
                </MDButton>
              )}
            </MDBox>
          </MDBox>
        </Card>

        {/* Fixed Save as Draft Button */}
        {!isViewMode && (
          <MDBox sx={{ position: "fixed", bottom: 16, right: 20, zIndex: 1200 }}>
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={handleSaveAsDraft}
              disabled={savingDraft || saving || !canEdit}
              sx={{
                background: properties.colors.bgcolorbluethem,
                boxShadow: "0px 4px 12px rgba(0,0,0,0.25)",
                px: 2,
                py: 0.75,
              }}
            >
              {savingDraft ? <CircularProgress size={16} color="inherit" /> : "💾 Save as Draft"}
            </MDButton>
          </MDBox>
        )}

        {/* Confirm Submit Dialog */}
        <Dialog
          open={openConfirmDialog}
          onClose={() => setOpenConfirmDialog(false)}
          disableScrollLock
          PaperProps={{ sx: { borderRadius: 2, minWidth: 360 } }}
        >
          <DialogTitle sx={{ fontWeight: "bold", color: "#1976d2", pb: 0.5, fontSize: "1rem" }}>
            Confirm Submission
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: "#333", fontSize: "0.88rem" }}>
              Are you sure you want to submit your self-assessment?
              <br />
              <br />
              <strong style={{ color: "#d32f2f" }}>Warning:</strong> Once submitted, you cannot edit
              this form again.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 1.25 }}>
            <MDButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => setOpenConfirmDialog(false)}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="success"
              size="small"
              onClick={handleConfirmSubmit}
              sx={{ background: properties.colors.bgcolorbluethem }}
            >
              Yes, Submit
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Notification */}
        {notification.open && (
          <MDBox sx={{ position: "fixed", top: 16, right: 16, zIndex: 9999, minWidth: 280 }}>
            <Alert
              severity={notification.type}
              onClose={() => setNotification({ open: false, message: "", type: "" })}
              sx={{ fontSize: "0.82rem" }}
            >
              {notification.message}
            </Alert>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default EmployeePerformanceForm;